import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, X, Store, Home, MapPin, Warehouse, Plus, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getAuthSession } from "@/lib/supabaseAuth";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";

const categories = [
  { id: "economico", name: "Accesibles", sub: "$2,000 - $8,000", icon: Store },
  { id: "local", name: "Locales",sub: "" ,icon: Store },
  { id: "oficina", name: "Oficinas / Consultorios", sub: "", icon: Store },
  { id: "casa", name: "Casas para negocio", sub: "", icon: Home },
  { id: "terreno", name: "Terrenos", sub: "", icon: MapPin },
  { id: "bodega", name: "Bodegas", sub: "", icon: Warehouse },
];

export const Header = ({ onCategoryChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [session, setSession] = useState(() => getAuthSession());
  const navigate = useNavigate();
  const userName =
    session?.user?.user_metadata?.nombre ||
    session?.user?.user_metadata?.full_name ||
    session?.user?.user_metadata?.name ||
    session?.user?.email ||
    "";

  useEffect(() => {
    const syncSession = () => setSession(getAuthSession());
    window.addEventListener("tulocalmx-auth-change", syncSession);
    window.addEventListener("storage", syncSession);
    return () => {
      window.removeEventListener("tulocalmx-auth-change", syncSession);
      window.removeEventListener("storage", syncSession);
    };
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsOpen(false);
  };

  const handleCategoryClick = (category) => {

    if (category.id === "economico") {
      navigate("/economicos");
      setIsOpen(false);
      return;
    }

    if (category.id === "local") {
      navigate("/locales");
      setIsOpen(false);
      return;
    }

    if (category.id === "oficina") {
      navigate("/oficinas");
      setIsOpen(false);
      return;
    }

    if (category.id === "casa") {
      navigate("/casas");
      setIsOpen(false);
      return;
    }

    if (category.id === "terreno") {
      navigate("/terrenos");
      setIsOpen(false);
      return;
    }

    if (category.id === "bodega") {
      navigate("/bodegas");
      setIsOpen(false);
      return;
    }

    onCategoryChange?.(category.id);
    scrollToSection("properties-section");
  };

  const handlePublishClick = () => {
    if (!getAuthSession()?.access_token) {
      navigate("/publicar-gratis?plan=gratis");
      setIsOpen(false);
      return;
    }

    navigate("/registro?plan=gratis");
    setIsOpen(false);
  };

  const handleLoginClick = () => {
    if (getAuthSession()?.access_token) {
      setIsOpen(false);
      return;
    }

    navigate("/iniciar-sesion");
    setIsOpen(false);
  };

  const handleAccountClick = () => {
    navigate("/mi-perfil");
    setIsOpen(false);
  };

return (
  <>
  <header className="sticky top-0 z-50 w-full backdrop-blur-xl bg-white/80 border-b border-gray-200/50 overflow-x-hidden">

    <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">

      <div className="flex items-center justify-between h-16">

        {/* LOGO */}
        <a href="/" className="flex items-center gap-2 flex-shrink-0 h-16">

          <img
            src="/logos/image.svg"
            alt="Tulocalmx"
            className="h-8 md:h-9 w-auto block "
          />

          <span className="text-sm md:text-base font-bold whitespace-nowrap leading-none">
            <span className="text-[#0056D2]">TULOCAL</span><span className="text-[#F97316]">MX</span>
          </span>

        </a>

        {/* DESKTOP MENU */}
        <nav className="hidden md:flex items-center gap-1 ml--0 flex-nowrap whitespace-nowrap">

<button
  onClick={() => navigate("/")}
  className="px-2 py-1 text-xs font-medium text-gray-600 hover:text-[#0056D2] whitespace-nowrap"
>
  Inicio
</button>
        {categories.map((category) => (
  <button
    key={category.id}
    onClick={() => handleCategoryClick(category)}
    className="flex flex-col items-start px-2 py-1 text-gray-600 hover:text-[#0056D2] whitespace-nowrap"
  >
    <span className="text-xs font-medium">
      {category.name}
    </span>

    {category.sub && (
      <span className="text-[8px] text-orange-500 font-semibold leading-none">
        {category.sub}
      </span>
    )}
  </button>
))}

          {/* CONTACTO */}
          <a
            href="https://wa.me/526673093018?text=Hola,%20quiero%20informacion%20sobre%20los%20locales"
            target="_blank"
            rel="noopener noreferrer"
            className="px-2 py-1 text-xs font-medium text-gray-600 hover:text-[#0056D2] whitespace-nowrap"
          >
            Contacto
          </a>

        </nav>

        {/* CTA DESKTOP */}
        <div className="hidden md:flex items-center gap-2">
          {userName && (
            <button
              type="button"
              onClick={handleAccountClick}
              className="max-w-[170px] truncate rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              {userName}
            </button>
          )}

          <button
            onClick={handlePublishClick}
            className="inline-flex h-8 items-center justify-center rounded-lg border border-slate-200 bg-white px-3 text-xs font-bold text-slate-700 shadow-sm transition hover:bg-slate-50"
          >
            Publicar gratis
          </button>

          <button
            onClick={handleLoginClick}
            className="inline-flex h-8 items-center justify-center gap-1 rounded-lg bg-[#F97316] px-3 text-xs font-bold text-white shadow-sm transition hover:bg-orange-600"
          >
            <LogIn className="w-3 h-3 mr-1" />
            Ingresar
          </button>

        </div>

        {/* MOBILE MENU */}
        <div className="flex items-center gap-2 md:hidden">
          {userName && (
            <button
              type="button"
              onClick={handleAccountClick}
              className="max-w-[120px] truncate rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-bold text-slate-700 shadow-sm"
            >
              {userName}
            </button>
          )}

        <Sheet open={isOpen} onOpenChange={setIsOpen}>

          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="md:hidden">
              <Menu className="h-6 w-6" />
            </Button>
          </SheetTrigger>

          <SheetContent side="right" className="w-full sm:w-80 p-0">

            <div className="flex flex-col h-full">

              <div className="flex items-center justify-between p-4 border-b">
                <span className="font-bold">
                  <span className="text-[#0056D2]">TULOCAL</span><span className="text-[#F97316]">MX</span>
                </span>

                <SheetClose asChild>
                  <Button variant="ghost" size="icon">
                    <X />
                  </Button>
                </SheetClose>

              </div>

              <nav className="flex-1 p-4 space-y-2">

                <button
                  onClick={() => scrollToSection("properties-section")}
                  className="w-full flex items-center gap-3 px-4 py-3"
                >
                  <Home className="w-5 h-5 text-[#0056D2]" />
                  Inicio
                </button>

                {categories.map((category) => (
                  <SheetClose asChild key={category.id}>
                    <button
                      onClick={() => handleCategoryClick(category)}
                      className="w-full flex items-center gap-3 px-4 py-3"
                    >
                      <category.icon className="w-5 h-5 text-[#0056D2]" />
                      {category.name}
                    </button>
                  </SheetClose>
                ))}

                <a
                  href="https://wa.me/526673093018?text=Hola,%20quiero%20informacion%20sobre%20los%20locales"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center gap-3 px-4 py-3"
                >
                  <MapPin className="w-5 h-5 text-[#0056D2]" />
                  Contacto
                </a>

                <button
                  onClick={() => {
                    handlePublishClick();
                  }}
                  className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg bg-[#F97316] px-4 py-3 font-bold text-white shadow-sm"
                >
                  <Plus className="w-5 h-5" />
                  Publicar gratis
                </button>

                <button
                  onClick={() => {
                    handleLoginClick();
                  }}
                  className="flex w-full items-center justify-center rounded-lg border border-slate-200 px-4 py-3 font-bold text-slate-700"
                >
                  Ingresar
                </button>

                {userName && (
                  <button
                    type="button"
                    onClick={handleAccountClick}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-center text-sm font-bold text-slate-700"
                  >
                    {userName}
                  </button>
                )}

              </nav>

            </div>

          </SheetContent>

        </Sheet>
        </div>

      </div>

    </div>

  </header>
  </>

  );
};

export default Header;



